import React, { useState, useRef, useCallback, useEffect, useLayoutEffect, useMemo } from 'react';
import classNames from 'classnames';
import { Box, Button, Card } from 'rebass';
import OutsideClickHandler from 'react-outside-click-handler';
import { FiChevronDown, FiDelete, FiEdit, FiTrash2 } from 'react-icons/fi';
import { Checkbox, Input, Label, Radio, Textarea } from '@rebass/forms';
import isEqual from 'lodash/isEqual';

import Portal from 'components/Portal';
import { PositionerV2 } from 'components/Positioner';
import { ExpandableRowScope } from 'components/ExpandableText/rowScope';

const TableContext = React.createContext({
    resizable: false,
    locked: false
});

const useTable = () => {
    const context = React.useContext(TableContext);

    if (context === undefined) {
        throw new Error('useTable must be used within a TableProvider');
    }

    return context;
};

/**
 * A Table component for rendering tabular data with customizable features like size, style, and interactivity.
 *
 * @param {Object} props - The props to configure the Table component.
 * @param {React.ReactNode} props.children - The content to be rendered inside the table.
 * @param {string} [props.className] - Additional custom class names to style the table.
 * @param {Object} [props.style] - Inline styles to apply to the table container.
 * @param {boolean} [props.isEditable] - Determines if the table rows are editable.
 * @param {'sm' | 'xs' | 'md' | 'lg'} [props.size='md'] - The size of the table. Can be 'sm', 'md', or 'lg'.
 * @param {boolean} [props.resizable=false] - Indicates if the table columns are resizable.
 * @param {boolean} [props.rowHoverable=true] - Enables or disables row hover effect.
 * @return {JSX.Element} The rendered Table component.
 */
function Table({
    children,
    className,
    style,
    isEditable,
    size = 'md',
    resizable = false,
    fixed = false,
    rowHoverable = true
}) {
    const [locked, setLocked] = useState(false);

    return (
        <TableContext.Provider value={{ resizable, locked, setLocked }}>
            <Box style={style} className={classNames('table-container', className)}>
                <Box
                    as="table"
                    className={classNames(
                        'table',
                        size === 'xs' && 'table-xs',
                        size === 'sm' && 'table-sm',
                        size === 'lg' && 'table-lg',
                        rowHoverable && 'table-row-hoverable',
                        isEditable && 'table-editable',
                        resizable && 'table-resizable',
                        fixed && 'table-fixed'
                    )}
                >
                    {children}
                </Box>
            </Box>
        </TableContext.Provider>
    );
}

/**
 * Table Head component.
 *
 * @param {Object} param0
 * @param {React.ReactNode} param0.children
 * @param {string} param0.className
 * @param {React.CSSProperties} param0.style
 */
function Head({ children, className, style }) {
    return (
        <Box as="thead" style={style} className={classNames('thead', className)}>
            {children}
        </Box>
    );
}

/**
 * Table Row component.
 *
 * @param {Object} param0
 * @param {React.ReactNode} param0.children
 * @param {string} param0.className
 * @param {React.CSSProperties} param0.style
 * @param {() => void} onClick
 */
function Tr({ children, className, style, onClick }) {
    return (
        <Box as="tr" style={style} className={classNames('tr', className)} onClick={onClick}>
            {children}
        </Box>
    );
}

function DefaultSortIcon({ sortOrder }) {
    return <FiChevronDown className={classNames(sortOrder === 'asc' && 'rotate-180')} />;
}

function Th({ children, className, style, sorter, onSort, sortOrder, onResize, sortIcon = DefaultSortIcon }) {
    const { resizable } = useTable();
    const [isDragging, setIsDragging] = useState(false);
    const startXRef = useRef(0); // It saves the start X position of the mouse when the user starts dragging.
    const startWidthRef = useRef(0); // It saves the start width of the column when the user starts dragging.

    const handleDragStart = useCallback(e => {
        // If the table is not resizable, do not allow the user to drag the column.
        if (!resizable) return;

        setIsDragging(true);
        startXRef.current = e.clientX;
        startWidthRef.current = e.target.parentElement.offsetWidth;
    }, []);

    const handleDragEnd = useCallback(() => {
        setIsDragging(false);
    }, []);

    const handleDrag = useCallback(
        e => {
            if (isDragging) {
                const diff = e.clientX - startXRef.current; // The difference between the start and current mouse position
                const newWidth = startWidthRef.current + diff;

                onResize(newWidth);
            }
        },
        [isDragging]
    );

    const getNextSortOrder = () => {
        if (sortOrder === 'asc') return 'desc';

        if (sortOrder === 'desc') return null;

        return 'asc';
    };

    useEffect(() => {
        if (isDragging) {
            document.addEventListener('mousemove', handleDrag);
            document.addEventListener('mouseup', handleDragEnd);
        }
        return () => {
            document.removeEventListener('mousemove', handleDrag);
            document.removeEventListener('mouseup', handleDragEnd);
        };
    }, [isDragging, handleDrag, handleDragEnd]);

    return (
        <Box
            as="th"
            className={classNames(
                'th',
                sorter && 'cursor-pointer',
                sorter && '!hover-black',
                sortOrder && '!text-primary',
                className
            )}
            style={{ position: 'relative', ...style }}
            onClick={() => sorter && onSort(getNextSortOrder())}
        >
            {children}
            {sorter && <span className="ml-1">{sortIcon({ sortOrder })}</span>}
            {resizable && (
                <div
                    className={classNames('th-resize-handler', isDragging && 'th-resize-handler-active')}
                    onClick={e => e.stopPropagation()}
                    onMouseDown={handleDragStart}
                />
            )}
        </Box>
    );
}

/**
 * Table Body component.
 *
 * @param {Object} param0
 * @param {React.ReactNode} param0.children
 * @param {string} param0.className
 * @param {React.CSSProperties} param0.style
 */
function Body({ children, className, style }) {
    return (
        <Box as="tbody" style={style} className={classNames('tbody', className)}>
            {children}
        </Box>
    );
}

const MIN_EDITOR_WIDTH = 300;

/**
 * Component to position a relative to the anchor element.
 *
 * @param {{ current: HTMLElement }} anchor Reference to the anchor element
 * @param {ReactNode} children Content to be displayed
 */
export function EditorPositioner({ anchorRef, children }) {
    /**
     * @type {React.MutableRefObject<HTMLDivElement>}
     */
    const containerRef = useRef(null);

    useLayoutEffect(() => {
        const container = containerRef.current;

        if (!containerRef || !anchorRef) return;

        // Function to position the container
        const positionContainer = () => {
            const client = anchorRef.current.getBoundingClientRect();
            const position = {
                left: client.left,
                top: client.top
            };

            const containerWidth = container.clientWidth > MIN_EDITOR_WIDTH ? container.clientWidth : MIN_EDITOR_WIDTH;
            const containerHeight = container.clientHeight;
            const isOutRight = position.left + containerWidth > window.innerWidth;
            const isOutBottom = position.top + containerHeight > window.innerHeight + window.scrollY;
            const isOutTop = anchorRef.current.top - containerHeight < 0;

            if (isOutRight) {
                position.left = position.left + anchorRef.current.clientWidth - containerWidth;
            }

            if (isOutBottom && !isOutTop) {
                position.top = position.top - containerHeight + anchorRef.current.clientHeight;
            }

            // Apply the calculated positions
            container.style.left = `${position.left}px`;
            container.style.top = `${position.top}px`;
            container.style.width = `${containerWidth}px`;
            container.style.minWidth = `${MIN_EDITOR_WIDTH}px`;
        };

        // Initial positioning
        positionContainer();

        // Set up ResizeObserver to detect height changes
        const resizeObserver = new ResizeObserver(() => {
            positionContainer();
        });

        // Start observing the container
        resizeObserver.observe(container);

        // Clean up
        return () => {
            resizeObserver.disconnect();
        };
    }, []);

    return (
        <div ref={containerRef} style={{ position: 'absolute' }}>
            {children}
        </div>
    );
}

const useShadow = value => {
    const [_value, _setValue] = useState(null);

    useEffect(() => {
        if (!isEqual(value, _value)) {
            _setValue(value);
        }
    }, [value]);

    return [_value, _setValue];
};

function Td({
    children,
    className,
    style,
    editable,
    onChange,
    value,
    editRender,
    onDelete,
    onEdit,
    turnOffDeleting,
    disableEditing,
    disableDeleting
}) {
    const ref = useRef(null);
    const [_value, _setValue] = useShadow(value);
    const [isEditing, setIsEditing] = useState(false);
    const [isOpen, setIsOpen] = useState(false);
    const context = useTable();

    const openEditor = () => {
        setIsOpen(true);
    };

    const handleChange = () => {
        if (context.locked) return;

        closeEditor();

        // Check if value has changed
        if (isEqual(_value, value)) return;

        onChange && onChange(_value);
    };

    const handleEdit = event => {
        event.stopPropagation();

        setIsEditing(true);
        onEdit && onEdit();
    };

    const handleDelete = event => {
        event.stopPropagation();

        onDelete && onDelete();
        closeEditor();
    };

    const closeEditor = () => {
        setIsEditing(false);
        setIsOpen(false);
    };

    return (
        <Box
            ref={ref}
            onClick={openEditor}
            as="td"
            style={style}
            className={classNames('td', editable && ' td-editable cursor-pointer', className)}
        >
            {children}
            {editable && (
                <>
                    <Portal isOpen={isOpen && !isEditing}>
                        <PositionerV2 anchorRef={ref}>
                            <OutsideClickHandler onOutsideClick={() => setIsOpen(false)}>
                                <Card className="!rounded-lg" style={{ width: `${MIN_EDITOR_WIDTH}px` }}>
                                    <Button
                                        variant="transparent-icon"
                                        className="block !w-full !text-left mr-2"
                                        onClick={handleEdit}
                                        disabled={disableEditing}
                                    >
                                        <FiEdit className="!m-0 !align-text-top" /> Edit
                                    </Button>
                                    {!!value && !turnOffDeleting && (
                                        <Button
                                            variant="transparent-icon"
                                            className="block !w-full !text-left !color-danger mr-2"
                                            onClick={handleDelete}
                                            disabled={disableDeleting}
                                        >
                                            <FiDelete className="!m-0 !align-text-top" /> Delete
                                        </Button>
                                    )}
                                </Card>
                            </OutsideClickHandler>
                        </PositionerV2>
                    </Portal>
                    {editable !== 'modal' && editable && (
                        <Portal isOpen={isEditing}>
                            <OutsideClickHandler onOutsideClick={handleChange}>
                                <EditorPositioner anchorRef={ref}>
                                    <Card className="!rounded-lg w-full h-full">
                                        {editRender && isEditing && (
                                            <>
                                                {editRender(
                                                    {
                                                        value: _value,
                                                        onChange: _setValue,
                                                        onCancel: event => {
                                                            event && event.stopPropagation();
                                                            closeEditor();
                                                        },
                                                        onSave: event => {
                                                            event && event.stopPropagation();
                                                            handleChange();
                                                        }
                                                    },
                                                    context
                                                )}
                                                <button
                                                    data-testId="editor-change-button"
                                                    type="button"
                                                    tabIndex={0}
                                                    className="sr-only"
                                                    onClick={handleChange}
                                                />
                                            </>
                                        )}
                                    </Card>
                                </EditorPositioner>
                            </OutsideClickHandler>
                        </Portal>
                    )}
                    {editable === 'modal' &&
                        isEditing &&
                        editRender &&
                        editRender(
                            {
                                value: _value,
                                onChange: _setValue,
                                onCancel: event => {
                                    event && event.stopPropagation();
                                    closeEditor();
                                },
                                onSave: event => {
                                    event && event.stopPropagation();
                                    handleChange();
                                }
                            },
                            context
                        )}
                </>
            )}
        </Box>
    );
}

/**
 * The newest version of the table component with centralized logic.
 *
 * @param {Array<{
 *     key: string
 *     width?: string | number
 *     minWidth?: string | number
 *     headerProps?: {
 *         className?: string
 *         style?: React.CSSProperties
 *     }
 *     editable?: boolean | 'modal'
 *     editRender?: (props: { value: any, onChange: (value: any) => void, onCancel: (event?: Event) => void, onSave: (event?: Event) => void }, context: { locked: boolean, setLocked: (locked: boolean) => void }) => React.ReactNode
 *     onCell?: (record: any) => {
 *         style?: React.CSSProperties
 *         className?: string
 *         value?: any
 *         onChange?: (value: any) => void
 *         onDelete?: () => void
 *         onEdit?: () => void
 *         turnOffDeleting?: boolean
 *         disableEditing?: boolean
 *         disableDeleting?: boolean
 *     }
 *     title: string | React.ReactNode
 *     sorter?: boolean
 *     sortOrder?: 'asc' | 'desc' | null
 *     onSort?: (sortOrder: 'asc' | 'desc' | null) => void
 *     render: (record: any) => string | React.ReactNode
 * }>} columns
 * @param {boolean} [resizable]
 * @param {{ id: any }[]} data
 * @param {(record: any) => {
 *     onError?: (error: Error) => string | React.ReactNode
 *     className?: string
 *     onClick?: (event: EventTarget) => void
 * }} [onRow]
 * @param {string} [className]
 * @param {'xs' | 'sm' | 'md' | 'lg'} [size]
 * @param {boolean} [isEditable]
 * @param {React.CSSProperties} [style]
 * @param {boolean} [rowHoverable]
 * @param {{ className?: string; style?: React.CSSProperties }} [headerProps]
 */
export function TableV2({
    columns,
    resizable,
    fixed,
    data,
    onRow,
    className,
    size,
    isEditable,
    style,
    rowHoverable,
    headerProps
}) {
    const cols = useRef({});

    /**
     * Resize the column by modifying the column reference
     *
     * @param key Column key
     * @param width New column width
     */
    const resize = (key, width) => {
        const col = cols.current[key];
        col.style.width = `${width}px`;
    };

    return (
        <Table
            resizable={resizable}
            fixed={fixed}
            size={size}
            className={classNames(className)}
            isEditable={isEditable}
            style={style}
            rowHoverable={rowHoverable}
        >
            <colgroup>
                {columns.map(({ key, width, minWidth }) => (
                    <col ref={ref => (cols.current[key] = ref)} key={key} style={{ width, minWidth }} />
                ))}
            </colgroup>
            <Head {...headerProps}>
                <Tr>
                    {columns.map(column => (
                        <Th
                            key={column.key}
                            {...(column.headerProps ? column.headerProps : {})}
                            width={column.width}
                            minWidth={column.minWidth}
                            sorter={column.sorter}
                            sortOrder={column.sortOrder}
                            onSort={column.onSort}
                            onResize={newWidth => resize(column.key, newWidth)}
                        >
                            {column.title}
                        </Th>
                    ))}
                </Tr>
            </Head>
            <Body>
                {data.map(record => {
                    try {
                        return (
                            <Tr key={record.id} {...(onRow ? onRow(record) : {})}>
                                {/* A record's long values are long together, so expanding
                                    one cell expands the row (PFOX-1406). Renders no element
                                    of its own — a <tr> may only contain cells. */}
                                <ExpandableRowScope>
                                    {columns.map(column => (
                                        <Td
                                            key={column.key}
                                            editRender={column.editRender}
                                            editable={column.editable}
                                            {...(column.onCell ? column.onCell(record) : {})}
                                        >
                                            {column.render(record)}
                                        </Td>
                                    ))}
                                </ExpandableRowScope>
                            </Tr>
                        );
                    } catch (error) {
                        const row = onRow ? onRow(record) : {};
                        return row.onError ? row.onError(error) : null;
                    }
                })}
            </Body>
        </Table>
    );
}

Table.Head = Head;
Table.Tr = Tr;
Table.Th = Th;
Table.Body = Body;
Table.Td = Td;

export { Table, Head, Tr, Th, Body, Td };
export default Table;
